import { useMemo } from 'react';
import { get } from 'lodash';

import { useMergedActivityLogs } from '../../queries/activityLogQuery';
import { useWriteOffItems } from '../../queries/writeOffsQuery';
import { useGetInvoice } from '../../queries/invoiceQuery';
import { ActivityDrawer } from '../common/ActivityDrawer';
import { statusMap, WriteOffTabType } from '../../types';

interface WriteOffActivityProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber: string;
  tabType: WriteOffTabType;
}

export const WriteOffActivity = ({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
  tabType,
}: WriteOffActivityProps) => {
  // Fetch write-off items to get all writeOffIds for this invoice
  const { data: writeOffItemsData } = useWriteOffItems(invoiceId);

  const writeOffIds =
    writeOffItemsData?.data
      ?.filter(item => item.status === statusMap[tabType])
      .map(item => item.writeOffId) ?? [];

  const {
    data: activityData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isActivityLoading,
  } = useMergedActivityLogs(invoiceId, writeOffIds, isOpen);

  // Fetch invoice to get commentHistory
  const { data: invoice } = useGetInvoice(invoiceId, {
    enabled: isOpen,
  });

  // Re-sort all accumulated activities to ensure correct chronological order
  // This handles pagination edge cases where later pages may contain older items
  const activities = useMemo(() => {
    const allActivities = activityData?.pages.flatMap(page => page.data) || [];
    return allActivities.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [activityData]);

  const commentHistory = get(invoice, 'commentHistory') as string | undefined;

  // Get commentHistory from first writeOff (similar to InvoiceActivity)
  const firstWriteOff = writeOffItemsData?.data?.find(
    item => item.status === statusMap[tabType]
  );
  const writeOffCommentHistory = get(firstWriteOff, 'commentHistory') as string | undefined;

  return (
    <ActivityDrawer
      isOpen={isOpen}
      onClose={onClose}
      invoiceNumber={invoiceNumber}
      activities={activities}
      isInitialLoading={isActivityLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={!!hasNextPage}
      fetchNextPage={fetchNextPage}
      commentHistory={commentHistory}
      writeOffCommentHistory={writeOffCommentHistory}
    />
  );
};
